import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import api, { API_BASE_URL } from "../services/api";

interface SignatureRecord {
  _id: string;
  user_id: {
    _id: string;
    name: string;
    email: string;
  };
  document_id: {
    _id: string;
    title: string;
    document_for: string;
  };
  envelope_id?: string;
  envelope_status?: string;
  is_signed: boolean;
  signed_at?: string;
  signed_document_link?: string;
  created_at: string;
}

interface SignatureStats {
  total: number;
  signed: number;
  pending: number;
  completion_rate: number;
}

const SignatureTracker: React.FC = () => {
  const [signatures, setSignatures] = useState<SignatureRecord[]>([]);
  const [stats, setStats] = useState<SignatureStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSignatures = async () => {
    try {
      setLoading(true);
      const response = await api.get("/signatures");
      setSignatures(response.data);
      setError(null);
    } catch (err: any) {
      setError("Failed to fetch signature records");
      console.error("Error fetching signatures:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get("/signatures/stats");
      setStats(response.data);
    } catch (err: any) {
      console.error("Error fetching signature stats:", err);
    }
  };

  useEffect(() => {
    fetchSignatures();
    fetchStats();
  }, []);

  const getStatusColor = (status?: string, isSigned?: boolean) => {
    if (isSigned) return "success";

    switch (status) {
      case "sent":
        return "info";
      case "delivered":
        return "warning";
      case "completed":
        return "success";
      case "declined":
        return "error";
      case "voided":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status?: string, isSigned?: boolean) => {
    if (isSigned) return "Signed";

    switch (status) {
      case "sent":
        return "Sent";
      case "delivered":
        return "Delivered";
      case "completed":
        return "Completed";
      case "declined":
        return "Declined";
      case "voided":
        return "Voided";
      default:
        return "Pending";
    }
  };

  const handleRefresh = () => {
    fetchSignatures();
    fetchStats();
  };

  const handleDownload = (signedDocumentLink: string) => {
    // Open PDF in new window/tab
    const url = `${API_BASE_URL}/${signedDocumentLink}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading && signatures.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Signature Tracker
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
          <Box flex="1" minWidth="200px">
            <Card>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Total Signatures
                </Typography>
                <Typography variant="h4" component="div">
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box flex="1" minWidth="200px">
            <Card>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Completed
                </Typography>
                <Typography variant="h4" component="div" color="success.main">
                  {stats.signed}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box flex="1" minWidth="200px">
            <Card>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Pending
                </Typography>
                <Typography variant="h4" component="div" color="warning.main">
                  {stats.pending}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box flex="1" minWidth="200px">
            <Card>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Completion Rate
                </Typography>
                <Typography variant="h4" component="div" color="info.main">
                  {stats.completion_rate.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Signatures Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Document</TableCell>
              <TableCell>Document Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Envelope ID</TableCell>
              <TableCell>Assigned Date</TableCell>
              <TableCell>Signed Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {signatures.map((signature) => (
              <TableRow key={signature._id}>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">
                      {signature.user_id.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {signature.user_id.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2">
                    {signature.document_id.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={signature.document_id.document_for}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusText(
                      signature.envelope_status,
                      signature.is_signed
                    )}
                    color={getStatusColor(
                      signature.envelope_status,
                      signature.is_signed
                    )}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {signature.envelope_id || "N/A"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {new Date(signature.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {signature.signed_at
                      ? new Date(signature.signed_at).toLocaleDateString()
                      : "Not signed"}
                  </Typography>
                </TableCell>
                <TableCell>
                  {signature.signed_document_link && (
                    <Tooltip title="Download signed document">
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownload(signature.signed_document_link!)
                        }
                        color="primary"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {signatures.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="textSecondary">
            No signature records found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Assign some documents to users to see signature tracking here.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SignatureTracker;
